const PARTNER_TOKEN_KEY = 'partner_token';
const REFERRAL_CODE_KEY = 'referral_code';
const STORAGE_KEY = 'referral_attribution';

const RESERVED_PATH_SEGMENTS = new Set([
  '', 'login', 'signin', 'signup', 'register', 'app', 'r', 'auth', 'getting-started', 'terms', 'privacy', 'cookie-policy',
  'account', 'settings', 'profile', 'refer', 'referrals', 'dashboard', 'chat', 'admin', 'advisor', 'agent', 'welcome', 'success', 'intake', 'organizer',
]);

export function getStoredPartnerToken() {
  try {
    return localStorage.getItem(PARTNER_TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setStoredPartnerToken(token) {
  try {
    if (token != null && token !== '') {
      localStorage.setItem(PARTNER_TOKEN_KEY, String(token).trim());
    } else {
      localStorage.removeItem(PARTNER_TOKEN_KEY);
    }
  } catch {}
}

/** First-touch: only set partner token if not already set (e.g. when landing on /ABCDE). */
export function setPartnerTokenIfEmpty(token) {
  if (!token || !String(token).trim()) return;
  try {
    if (localStorage.getItem(PARTNER_TOKEN_KEY)) return;
    const value = String(token).trim();
    localStorage.setItem(PARTNER_TOKEN_KEY, value);
    if (!localStorage.getItem(REFERRAL_CODE_KEY)) localStorage.setItem(REFERRAL_CODE_KEY, value);
  } catch {}
}

/** Parse ref and UTM params from current URL. */
export function getAttributionFromUrl() {
  if (typeof window === 'undefined' || !window.location) return null;
  const { pathname, search } = window.location;
  const params = new URLSearchParams(search);
  let ref = params.get('ref') || null;
  const pathMatch = pathname.match(/^\/r\/([^/]+)$/);
  if (pathMatch) ref = ref || pathMatch[1];
  if (!ref && pathname.startsWith('/')) {
    const segment = pathname.slice(1).split('/')[0];
    if (segment && !RESERVED_PATH_SEGMENTS.has(segment.toLowerCase())) ref = segment;
  }
  const utm_source = params.get('utm_source') || null;
  const utm_medium = params.get('utm_medium') || null;
  const utm_campaign = params.get('utm_campaign') || null;
  const utm_term = params.get('utm_term') || null;
  const utm_content = params.get('utm_content') || null;
  if (!ref && !utm_source && !utm_medium && !utm_campaign && !utm_term && !utm_content) return null;
  return {
    ref: ref || null,
    utm_source, utm_medium, utm_campaign, utm_term, utm_content,
    timestamp: new Date().toISOString(),
  };
}

export function getStoredAttribution() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function persistAttribution(data) {
  if (!data || (!data.ref && !data.utm_source)) return;
  try {
    if (getStoredAttribution()) return;
  } catch {}
  const payload = {
    ref: data.ref || null,
    utm_source: data.utm_source || null,
    utm_medium: data.utm_medium || null,
    utm_campaign: data.utm_campaign || null,
    utm_term: data.utm_term || null,
    utm_content: data.utm_content || null,
    timestamp: data.timestamp || new Date().toISOString(),
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    if (payload.ref) {
      localStorage.setItem(PARTNER_TOKEN_KEY, payload.ref);
      localStorage.setItem(REFERRAL_CODE_KEY, payload.ref);
    }
  } catch {}
}

/** Capture attribution from URL and persist (first-touch). Call on app load / first visit. */
export function captureAttributionOnVisit() {
  const data = getAttributionFromUrl();
  if (data) persistAttribution(data);
}

export function getStoredReferralCode() {
  try {
    return localStorage.getItem(REFERRAL_CODE_KEY) || null;
  } catch {
    return null;
  }
}

export function setStoredReferralCode(code) {
  try {
    if (code != null && code !== '') {
      localStorage.setItem(REFERRAL_CODE_KEY, String(code).trim());
    } else {
      localStorage.removeItem(REFERRAL_CODE_KEY);
    }
  } catch {}
}

/** Parse ref from URL: ?ref=TOKEN or /r/TOKEN */
export function getRefFromUrl() {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const ref = params.get('ref');
  if (ref) return ref.trim();
  const match = window.location.pathname.match(/^\/r\/([^/]+)$/);
  return match ? match[1].trim() : null;
}

/**
 * Persist ref whenever present in URL so it survives logout and reloads.
 * New ref in URL overwrites any previously stored value.
 */
export function captureRefOnVisit() {
  const ref = getRefFromUrl();
  if (!ref) return;
  try {
    const value = String(ref).trim();
    if (value) {
      localStorage.setItem(PARTNER_TOKEN_KEY, value);
      localStorage.setItem(REFERRAL_CODE_KEY, value);
    }
  } catch {}
}

/**
 * Returns path with stored ref appended as query param, for post-logout and home redirects.
 * Avoids duplicate ref if path already contains ref. Uses localStorage (same as partner token).
 */
export function getPathWithRef(path) {
  const stored = getStoredPartnerToken();
  if (!stored) return path;
  const hasQuery = path.indexOf('?') !== -1;
  const [pathname, search] = hasQuery ? path.split('?', 2) : [path, ''];
  const params = new URLSearchParams(search || '');
  if (params.has('ref')) return path;
  params.set('ref', stored);
  const q = params.toString();
  return q ? `${pathname}?${q}` : pathname;
}
